import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Search } from 'lucide-react';
import axios from 'axios';
import { blogsMetadata as fallbackBlogs } from '../data/blogsMetadata';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { CONFIG } from '../utils/config';
import LoadingSkeleton from '../components/LoadingSkeleton';

export const parseFrontmatter = (content) => {
    const fmMatch = content.match(/^---\r?\n([\s\S]*?)\r?\n---(?:\r?\n)?/);
    if (!fmMatch) return { data: {}, content };
    const fmText = fmMatch[1];
    const data = {};
    fmText.split('\n').forEach(line => {
        const colonIdx = line.indexOf(':');
        if (colonIdx > -1) {
            const key = line.slice(0, colonIdx).trim();
            let val = line.slice(colonIdx + 1).trim();
            if (val.startsWith('"') && val.endsWith('"')) {
                val = val.slice(1, -1);
            }
            data[key] = val;
        }
    });
    return { data, content: content.slice(fmMatch[0].length) };
};

const Blog = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');

    useEffect(() => {
        window.scrollTo(0, 0);
        AOS.init({ duration: 800, once: true });

        const fetchBlogs = async () => {
            try {
                const res = await axios.get(`${CONFIG.API_BASE}/blogs`);
                if (res.data && res.data.length > 0) {
                    const parsedBlogs = res.data.map(b => {
                        const { data } = parseFrontmatter(b.content || "");
                        return {
                            id: b.id,
                            title: data.title || b.title,
                            slug: data.slug || b.id,
                            category: data.category || b.category || "Update",
                            readTime: data.read_time || "3 min",
                            image: data.cover_image || "/assets/blog/default_cover.webp",
                            excerpt: data.excerpt || b.content?.substring(0, 100) + '...',
                            author: data.author || b.author || "Doumene M. Rosvel",
                            date: data.date || b.created_at
                        };
                    });
                    setBlogs(parsedBlogs);
                } else {
                    setBlogs(fallbackBlogs);
                }
            } catch(e) {
                console.error("Failed to fetch blogs", e);
                setBlogs(fallbackBlogs);
            } finally {
                setLoading(false);
            }
        };
        fetchBlogs();
    }, []);

    // Get unique categories dynamically
    const categories = ['All', ...new Set(blogs.map(b => b.category))];

    // Filter logic
    const filteredBlogs = blogs.filter(blog => {
        const matchesSearch = 
            blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            blog.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
            blog.category.toLowerCase().includes(searchQuery.toLowerCase());
            
        const matchesCategory = selectedCategory === 'All' || blog.category === selectedCategory;
        
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="pt-28 md:pt-48 pb-16 min-h-screen relative">
            <Helmet>
                <title>Zaptopay Blog | Crypto Insights & Updates Cameroon</title>
                <meta name="description" content="Read the latest news, guides, and insights on P2P crypto trading, mobile money, and blockchain technology in Cameroon and CEMAC." />
            </Helmet>

            <div className="bg-blob" style={{ top: '10%', left: '10%' }}></div>

            <div className="container relative z-10">
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-6xl font-black mb-6 glow-text text-primary" data-aos="fade-down">
                        The Zaptopay Blog
                    </h1>
                    <p className="text-text-muted max-w-2xl mx-auto text-lg mb-8" data-aos="fade-up" data-aos-delay="100">
                        Expert insights, deep dives, and step-by-step guides for mastering crypto trading and P2P exchange in Central Africa.
                    </p>
                </div>

                {/* Search & Category Filter Section */}
                {!loading && blogs.length > 0 && (
                    <div className="max-w-6xl mx-auto mb-16 flex flex-col md:flex-row gap-6 items-center justify-between" data-aos="fade-up" data-aos-delay="200">
                        {/* Search Input Container */}
                        <div className="relative w-full md:w-96 group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted group-focus-within:text-primary transition-colors" size={18} />
                            <input
                                type="text"
                                placeholder="Search articles..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full bg-black/40 border border-white/10 rounded-xl pl-12 pr-6 py-4 text-sm font-medium text-white focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 transition-all group-hover:bg-primary/5 shadow-inner"
                            />
                        </div>

                        {/* Categories Horizontal Scroll Chips */}
                        <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-3 md:pb-0 scrollbar-none max-w-full">
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    className={`px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-300 whitespace-nowrap shrink-0 ${
                                        selectedCategory === cat
                                            ? 'bg-primary text-black shadow-[0_0_20px_rgba(0,255,136,0.3)] hover:scale-105'
                                            : 'bg-white/5 border border-white/10 text-white hover:border-primary/50 hover:bg-primary/5'
                                    }`}
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {loading && (
                        <div className="col-span-full">
                            <LoadingSkeleton text="Fetching live articles..." />
                        </div>
                    )}
                    {!loading && filteredBlogs.length === 0 && (
                        <div className="col-span-full text-center py-20 glass p-10 border border-white/5 rounded-2xl">
                            <p className="text-text-muted uppercase font-mono tracking-widest text-sm">No articles found matching your query</p>
                            <button 
                                onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}
                                className="mt-6 px-6 py-3 bg-primary text-black font-black uppercase text-xs tracking-wider rounded-xl hover:scale-105 transition-all shadow-[0_0_15px_rgba(0,255,136,0.2)]"
                            >
                                Reset Filters
                            </button>
                        </div>
                    )}
                    {!loading && filteredBlogs.map((blog, idx) => (
                        <Link 
                            to={`/blog/${blog.slug}`} 
                            key={blog.id}
                            className="glass p-6 group hover:border-primary/50 transition-all duration-300 relative overflow-hidden flex flex-col h-full"
                            data-aos="fade-up"
                            data-aos-delay={idx * 100}
                        >
                            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-primary/20 transition-colors"></div>
                            
                            {/* Card Thumbnail Image */}
                            <div className="relative h-48 w-full overflow-hidden rounded-xl mb-6 border border-white/5 z-10 bg-black/40">
                                <img 
                                    src={blog.image} 
                                    alt={blog.title} 
                                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                                    loading="lazy"
                                />
                            </div>

                            <div className="flex justify-between items-center mb-4 text-xs font-mono text-primary z-10">
                                <span className="uppercase tracking-widest font-black">{blog.category}</span>
                                <span>{blog.readTime}</span>
                            </div>
                            
                            <h2 className="text-xl font-bold mb-4 text-text-main group-hover:text-primary transition-colors z-10 leading-tight">
                                {blog.title}
                            </h2>
                            
                            <p className="text-text-muted mb-8 flex-grow z-10 text-sm leading-relaxed">
                                {blog.excerpt}
                            </p>
                            
                            <div className="flex items-center justify-between mt-auto z-10 border-t border-white/10 pt-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-primary to-secondary flex items-center justify-center text-black font-bold text-xs">
                                        DR
                                    </div>
                                    <div className="text-sm text-text-muted">
                                        {blog.author}
                                        <div className="text-xs opacity-70">{new Date(blog.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>
                                    </div>
                                </div>
                                
                                <span className="text-primary font-bold group-hover:translate-x-2 transition-transform">
                                    Read →
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Blog;
